import { inject, Injectable } from '@angular/core';
import {
  Answer,
  FileAction,
  FormStatus,
  LabAnalysisStatus,
  Page,
  PatientSmartFormSubmission,
  SectionAnswers,
  SmartFormAnalysisResult,
  SmartFormAnalysisReport,
  SmartFormAnalysisStatusResponse,
  SmartFormSubmission,
  TemplateFormSchema,
  SubmissionResults,
  SmartFormOrderBy,
  SMART_FORM_TEMPLATE_ID,
} from '@models';
import {
  SmartFormSubmissionService,
  SmartFormTemplateMockService,
} from '@services';
import { filter, map, Observable, of, switchMap } from 'rxjs';

const SENT_MOCK_ID = 'sent-mock-submission-1';
const OPENED_MOCK_ID = 'opened-mock-submission-1';
const COMPLETED_MOCK_ID = 'completed-mock-submission-1';

@Injectable()
export class SmartFormSubmissionMockService
  implements SmartFormSubmissionService
{
  private template = inject(SmartFormTemplateMockService);
  private readonly templateId = SMART_FORM_TEMPLATE_ID;
  private readonly templateVersion = 24;

  private MOCK_SUBMISSIONS = [
    {
      id: SENT_MOCK_ID,
      submissionId: SENT_MOCK_ID,
      templateId: this.templateId,
      templateVersion: this.templateVersion,
      templateName: 'Health Survey',
      patientId: 'p-1',
      patientName: 'John Doe',
      patientDisplayName: 'John Doe',
      providerId: 'prov-1',
      providerName: 'John Provider',
      status: FormStatus.Sent,
      analysisStatus: LabAnalysisStatus.AnalysisProcessing,
      sectionAnswers: [],
      percentageScore: 0,
      fileName: 'health-survey.pdf',
      createdAtUtc: new Date().toISOString(),
      updatedAtUtc: new Date().toISOString(),
      completedAtUtc: null,
      fillPercentage: 3,
    },
    {
      id: OPENED_MOCK_ID,
      submissionId: OPENED_MOCK_ID,
      templateId: this.templateId,
      templateVersion: this.templateVersion,
      templateName: 'Health Survey',
      patientId: 'p-1',
      patientName: 'John Doe',
      patientDisplayName: 'John Doe',
      providerId: 'prov-1',
      providerName: 'John Provider',
      status: FormStatus.Opened,
      analysisStatus: LabAnalysisStatus.AnalysisProcessing,
      sectionAnswers: [
        {
          sectionId: 'general',
          totalQuestions: 13,
          answeredQuestions: 1,
          percentageFinished: 8,
          answers: [
            {
              questionId: 'weight',
              type: 'Weight',
              valueNumber: 100,
            },
            {
              questionId: 'desired_weight',
              type: 'Weight',
              valueNumber: 100,
            },
          ],
        },
        {
          sectionId: 'major_symptoms',
          totalQuestions: 1,
          answeredQuestions: 1,
          percentageFinished: 100,
          answers: [
            {
              questionId: 'condition_symptoms',
              type: 'RepeatingTable',
              valueJson:
                '[{"condition":"Constipation","diagnosed_date":"2026-03-02"},{"condition":"Spasticity, spinal cord injury (Increase in muscle tone due to brain injury or neurodegenerative condition)","diagnosed_date":"2026-03-18"}]',
            },
          ],
        },
        {
          sectionId: 'health_and_wellness_goals',
          totalQuestions: 1,
          answeredQuestions: 1,
          percentageFinished: 100,
          answers: [
            {
              questionId: 'health_and_wellness_goals',
              type: 'RepeatingTable',
              valueJson:
                '[{"wellness_goal_text":"Goal 1","importance_order":"shortTerm"},{"wellness_goal_text":"Goal 2","importance_order":"immediate"},{"wellness_goal_text":"Goal 3","importance_order":"longTerm"}]',
            },
          ],
        },
        {
          sectionId: 'serious_accidents',
          totalQuestions: 1,
          answeredQuestions: 2,
          percentageFinished: 200,
          answers: [
            {
              questionId: 'are_u_taking_hospitalizations',
              type: 'YesNo',
              valueBool: true,
            },
            {
              questionId: 'hospitalizations_surgeries_accidents_followup',
              type: 'RepeatingTable',
              valueJson:
                '[{"accident_description":"Accident 1","accident_importance_order":"2026-03-03"}]',
            },
          ],
        },
        {
          answeredQuestions: 2,
          answers: [
            {
              questionId: 'current_medications',
              type: 'YesNo',
              valueBool: true,
            },
            {
              questionId: 'current_medications_followUp',
              type: 'RepeatingTable',
              valueJson:
                '[{"medication_name":"test","medication_start_date":"2026-02-28","medication_reason_for_use":"Some reason","medication_fruquency":"twice_daily","medication_dose_per_intake":"12","medication_form_of_intake":"injection"}]',
            },
          ] as Answer[],
          percentageFinished: 3,
          sectionId: 'current_medications',
          totalQuestions: 2,
        },
        {
          answeredQuestions: 2,
          answers: [
            {
              questionId: 'current_supplements',
              type: 'YesNo',
              valueBool: true,
            },
            {
              questionId: 'current_supplements_followup',
              type: 'RepeatingTable',
              valueJson:
                '[{"supplement_name":"Aluminum/       Magnesium","supplement_start_date":"2026-02-10","supplements_reason_for_use":"Some reason","supplements_frequency":"every_morning","supplements_dose_per_intake":"12","supplements_form_of_intake":"injection"}]',
            },
          ] as Answer[],
          percentageFinished: 3,
          sectionId: 'current_supplements',
          totalQuestions: 2,
        },
      ] as SectionAnswers[],
      percentageScore: 80,
      fileName: 'health-survey.pdf',
      createdAtUtc: new Date().toISOString(),
      updatedAtUtc: new Date().toISOString(),
      completedAtUtc: null,
      fillPercentage: 3,
    },
    {
      id: COMPLETED_MOCK_ID,
      submissionId: COMPLETED_MOCK_ID,
      templateId: this.templateId,
      templateVersion: this.templateVersion,
      templateName: 'Health Survey',
      patientId: 'p-1',
      patientName: 'John Doe',
      patientDisplayName: 'John Doe',
      providerId: 'prov-1',
      providerName: 'John Provider',
      status: FormStatus.Completed,
      analysisStatus: LabAnalysisStatus.Queued,
      sectionAnswers: [],
      percentageScore: 100,
      fileName: null,
      createdAtUtc: new Date().toISOString(),
      updatedAtUtc: new Date().toISOString(),
      completedAtUtc: null,
      fillPercentage: 3,
    },
  ];

  getSubmissions(_params?: {
    status?: FormStatus;
    page: number;
    pageSize: number;
    patientName?: string;
    orderBy?: SmartFormOrderBy;
    desk?: boolean;
  }): Observable<Page<SmartFormSubmission>> {
    return of({
      items: this.MOCK_SUBMISSIONS,
      totalItems: this.MOCK_SUBMISSIONS.length,
      pageNumber: 1,
      pageSize: 10,
      totalPages: 1,
    });
  }

  getPatientSubmissions(
    patientId: string
  ): Observable<PatientSmartFormSubmission[]> {
    return of(
      this.MOCK_SUBMISSIONS.filter(item => item.patientId === patientId).map(
        ({ id, templateName, status, createdAtUtc }) => ({
          id,
          templateName,
          status,
          createdAtUtc,
        })
      )
    );
  }

  getSubmissionById(id: string) {
    return this.getSubmissions().pipe(
      map(r => r.items.find(item => item.id === id)),
      filter((submission): submission is SmartFormSubmission => !!submission),
      switchMap(submission =>
        this.template.getTemplate(this.templateId, this.templateVersion).pipe(
          filter((template): template is TemplateFormSchema => !!template),
          map(template => ({
            definition: template,
            metadata: {
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              analysisStatus: LabAnalysisStatus.AnalysisProcessing,
              version: this.templateVersion,
            },
            submission,
          }))
        )
      )
    );
  }

  patchSubmission(id: string) {
    return this.getSubmissionById(id).pipe(map(res => res.submission));
  }

  deleteSubmission(submissionId: string): Observable<void> {
    this.MOCK_SUBMISSIONS = this.MOCK_SUBMISSIONS.filter(
      ({ id }) => id !== submissionId
    );

    return of(void 0);
  }

  getSubmissionResults() {
    return of({
      scoringResults: [
        {
          sectionId: 'nutritional_assessment',
          graphType: 'MoreIsBetter',
          maxPossibleScore: 129,
          score: 112,
          categoryScore: 9,
        },
      ],
      clinicalInsight: null,
      patientSurveySectionScore: [
        {
          patientSurveySubsectionId: 'string',
          protocolId: null,
          scoreDescription: null,
          score: 1,
        },
      ],
    } as SubmissionResults);
  }

  getSubmissionSmartFormAnalysis(id: string) {
    return of({
      id,
    } as SmartFormAnalysisReport);
  }

  getSubmissionSmartFormAnalysisStatus(id: string) {
    return of({
      submissionId: id,
      status: LabAnalysisStatus.Queued,
    } as SmartFormAnalysisStatusResponse);
  }

  shareSubmissionSmartFormAnalysisPatientHandout() {
    return of({});
  }

  updateSubmissionSmartFormAnalysisChartnoteSummary(
    _id: string,
    chartnotes: string
  ) {
    return of({
      summary: chartnotes,
    } as SmartFormAnalysisResult);
  }

  submitSubmission() {
    return of(void 0);
  }

  getPatientNameBySubmissionId(id: string): Observable<string> {
    return this.getSubmissionById(id).pipe(
      map(res => res.submission.patientName)
    );
  }

  sendReminder(id: string): Observable<void> {
    console.log(id);
    return of();
  }

  getSubmissionPdf(_id: string, _action: FileAction): Observable<Blob> {
    return of(new Blob([], { type: 'application/pdf' }));
  }

  getAnalysisPdf(_id: string, _action: FileAction): Observable<Blob> {
    return of(new Blob([], { type: 'application/pdf' }));
  }

  getSubmissionPdfWithMeta(
    _id: string,
    _action: FileAction
  ): Observable<{
    blob: Blob;
    fileName: string | null;
    disposition: string | null;
  }> {
    return of({
      blob: new Blob([], { type: 'application/pdf' }),
      fileName: null,
      disposition: null,
    });
  }
}
